import type {State} from 'src/reducers';
import type {
  CompositeAutomationNodeApi,
  Swimlane,
} from 'src/types/automation-workflow';

import {createSelector} from 'reselect';
import {selectDisplayConfigByKey} from 'src/selectors/display-config';
import {
  TEMP_NODE_IDENTIFIER,
  isTempNode,
  ACTIVE_EDITABLE_NODE_TYPES,
} from 'src/utils/automation-workflow';

/*
 * Base Selectors
 */

export const selectAutomationWorkflow = (
  state: State,
): {
  [key: string]: string;
  // @ts-expect-error - TS2339 - Property 'swimlanes' does not exist on type 'never'.
} => state.automationWorkflow;

export const selectSwimlane = (state: State, swimlaneId: string): Swimlane => {
  const swimlaneIdInt =
    typeof swimlaneId === 'string' ? parseInt(swimlaneId, 10) : swimlaneId;
  // @ts-expect-error - TS2339 - Property 'swimlanes' does not exist on type 'never'.
  return state.automationWorkflow.swimlanes.find(
    // @ts-expect-error - TS7006 - Parameter 'swimlane' implicitly has an 'any' type.
    (swimlane) => swimlane.id === swimlaneIdInt,
  );
};

export const selectIsTempNodePresent = (state: State): boolean =>
  state.automationWorkflow.swimlanes.some((swimlane) =>
    Object.keys(swimlane.nodes).find(
      (nodeId) => nodeId === TEMP_NODE_IDENTIFIER,
    ),
  );
export const selectAssociatedFilterCount = (state: State): number => {
  if (state.automationWorkflow.filters == null) {
    return 0;
  }
  const {excluded = [], included = []} = state.automationWorkflow.filters;
  return excluded.length + included.length;
};

export const selectAssociatedFilterIds = (state: State): Array<number> => {
  if (state.automationWorkflow.filters == null) {
    return [];
  }
  const {excluded = [], included = []} = state.automationWorkflow.filters;
  // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'string' and 'boolean' have no overlap.
  return [...excluded, ...included];
};

export const selectIsEditDisallowedForSettings: (state: State) => boolean =
  createSelector(
    selectAutomationWorkflow,
    (state) => selectDisplayConfigByKey(state, 'aep_workflow_edit'),
    (workflow, canEditWorkflow) =>
      workflow.status === 'ACTIVE' ||
      !canEditWorkflow ||
      // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'string' and 'boolean' have no overlap.
      workflow.locked === true,
  );

export const selectIsWorkflowEditAllowed: (state: State) => boolean =
  createSelector(
    selectAutomationWorkflow,
    (state) => selectDisplayConfigByKey(state, 'aep_workflow_edit'),
    (workflow, canEditWorkflow) => !!canEditWorkflow && !workflow.locked,
  );

export const selectIsWorkflowActive: (state: State) => boolean = (state) =>
  state.automationWorkflow.status === 'ACTIVE';

export const selectIsActiveWorkflowEditAllowedForNode: (
  state: State,
  nodeType: string,
) => boolean = createSelector(
  selectIsWorkflowActive,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'nodeType' implicitly has an 'any' type.
  (state, nodeType) => nodeType,
  (isActiveWorkflowEditAllowed, nodeType) =>
    isActiveWorkflowEditAllowed &&
    ACTIVE_EDITABLE_NODE_TYPES.includes(nodeType),
);

export const selectDisallowWorkflowActivate: (state: State) => boolean =
  createSelector(
    (state) => selectDisplayConfigByKey(state, 'aep_workflow_activate'),
    (canActivateWorkflow) => !canActivateWorkflow,
  );

export const selectTotalFilterCount: (
  state: State,
  swimlaneId: string,
  nodeId: string,
) => number | null | undefined = createSelector(
  (state) => state,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'swimlaneId' implicitly has an 'any' type.
  (state, swimlaneId) =>
    state.automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
      ({id}) => parseInt(swimlaneId, 10) === id,
    ),
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'nodeId' implicitly has an 'any' type.
  (_, __, nodeId) => nodeId,
  (state, swimlane, nodeId) => swimlane.nodes[nodeId]?.next_nodes.length,
);

export const selectEntityTypesForNode: (
  state: State,
  swimlaneId?: string | null | undefined,
  nodeId?: string | null | undefined,
) => {
  anchor_entity_type: string | null | undefined;
  trigger_entity_type: string | null | undefined;
} = createSelector(
  (state) => state,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'swimlaneId' implicitly has an 'any' type.
  (state, swimlaneId) =>
    state.automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
      ({id}) => parseInt(swimlaneId, 10) === id,
    ),
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'nodeId' implicitly has an 'any' type.
  (_, __, nodeId) => nodeId,
  (state, swimlane, nodeId) => {
    if (!nodeId || !swimlane) {
      return {anchor_entity_type: null, trigger_entity_type: null};
    }

    /**NOTE:(diwakersurya) we need to consider the previous node to decide the
     * entity types if this node has not been saved yet. On ui, we still need
     * to allow the user to select the ems attributes based on the previous node.
     */

    if (isTempNode(nodeId)) {
      const previousNodeId = Object.keys(swimlane.nodes).find((id) =>
        swimlane.nodes[id].next_nodes
          // @ts-expect-error - TS7031 - Binding element 'next_node' implicitly has an 'any' type.
          .map(({next_node}) => next_node)
          .includes(nodeId),
      );

      if (previousNodeId) {
        const previousNode = swimlane.nodes[previousNodeId];
        if (!previousNode) {
          return {anchor_entity_type: null, trigger_entity_type: null};
        }
        return {
          //if previous node has entity type,(e.g. foreach node) use it, else use previous node's anchor entity type
          anchor_entity_type:
            previousNode.body?.body?.entity_type ??
            previousNode.anchor_entity_type,
          trigger_entity_type: previousNode.trigger_entity_type,
        };
      }
    }

    const node = swimlane.nodes[nodeId];
    if (!node) {
      return {anchor_entity_type: null, trigger_entity_type: null};
    }
    const {anchor_entity_type, trigger_entity_type} = node;
    return {anchor_entity_type, trigger_entity_type};
  },
);

export const selectSwimlaneNode: (
  state: State,
  swimlaneId?: string | null | undefined,
  nodeId?: string | null | undefined,
) => CompositeAutomationNodeApi | null | undefined = createSelector(
  (state) => state,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'swimlaneId' implicitly has an 'any' type.
  (state, swimlaneId) =>
    state.automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
      ({id}) => parseInt(swimlaneId, 10) === id,
    ),
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'nodeId' implicitly has an 'any' type.
  (_, __, nodeId) => nodeId,
  (state, swimlane, nodeId) => {
    if (!nodeId || !swimlane) {
      return null;
    }

    return swimlane.nodes[nodeId];
  },
);

export const selectNwbTypesForTaskModule: (
  state: State,
  swimlaneId?: string | null | undefined,
  nodeId?: string | null | undefined,
) => Array<string> = createSelector(
  (state) => state,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'swimlaneId' implicitly has an 'any' type.
  (state, swimlaneId) =>
    state.automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
      ({id}) => parseInt(swimlaneId, 10) === id,
    ),
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'nodeId' implicitly has an 'any' type.
  (_, __, nodeId) => nodeId,
  (state, swimlane, nodeId) => {
    if (!nodeId || !swimlane) {
      return [];
    }
    if (swimlane.nodes[nodeId].next_nodes) {
      const nextNodeId = swimlane.nodes[nodeId].next_nodes[0].next_node;
      return swimlane.nodes[nextNodeId].metadata?.hidden_task_types ?? [];
    }
    return [];
  },
);

export const isWorkflowActivatedEver: (state: State) => boolean =
  createSelector(
    selectAutomationWorkflow,
    (workflow) => workflow.status !== 'INACTIVE',
  );

export const selectFilterDisplayName: (
  state: State,
  swimlaneId: string,
  nodeId: string,
  branchIndex: number,
) => string = createSelector(
  selectSwimlaneNode,
  (_: State, __: string, ___: string, branchIndex: number) => branchIndex,
  (swimlaneNode, branchIndex) => {
    if (!swimlaneNode) {
      return '';
    }
    return swimlaneNode.next_nodes?.[branchIndex]?.display_name ?? '';
  },
);

export const selectAllNodesByCompositeId = createSelector(
  (state: State) => state.automationWorkflow.swimlanes as Swimlane[],
  (swimlanes: Swimlane[]) => {
    const nodeMap: Record<string, CompositeAutomationNodeApi> = {};
    for (const swimlane of swimlanes) {
      for (const [nodeId, node] of Object.entries(swimlane.nodes)) {
        nodeMap[nodeId] = node as CompositeAutomationNodeApi;
      }
    }
    return nodeMap;
  },
);
